import Library from './scene/utility/Library';
import cutscenes from './library/cutscenes.json';
import milestonelist from './library/milestones.json';
import { saveAs } from 'file-saver';
import { updateSaveData } from './VersionUpdater';
import CryptoJS from 'crypto-js';
import { contentLimits, unlockedLevel, devotionLimit, storySceneLevels } from './scene/utility/championProgression';

const version = '8.9.2';
const DEBUG_SHOW_ALL_COLLECTION = false;
const DEBUG_START_FULLY_UNLOCKED = false;

function initialChampionData (model) {
  const limits = contentLimits(model, true);
  const devotion = (rank, selected=rank) => ({
    rank: DEBUG_START_FULLY_UNLOCKED ? rank : 1,
    selected: DEBUG_START_FULLY_UNLOCKED ? selected : 1,
    progress: 0
  });
  return {
    shards: 0,
    level: DEBUG_START_FULLY_UNLOCKED ? limits.level : 1,
    progress: DEBUG_START_FULLY_UNLOCKED ? limits.storyProgress : 1,
    benevolence: 0,
    devotion: {
      maze: devotion(limits.mazePower),
      // Development starting bonuses, independent of release content limits.
      ...Object.fromEntries(['cards', 'exp', 'passive', 'health'].map(key => [key, devotion(1, 1)]))
    },
    stats: { runsPlayed: 0, timePlayed: 0, runs: [] }
  };
}

const OAUTH_CLIENT_ID = "dEwJq9p7qYkYcczZOSplEbA6YEmRRprHDXgI2vKUlh8rT6-nBAf2QbLhCWj9lCLT";
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;

const champions = Library.champions;

// This release has no subscriber-exclusive content. Also bypasses startup membership checks.
let premium = true;

let items = { version }

const starterCardKeys = [
	"strike-amber", "defend-amber", "menace",
	"strike-ruby", "defend-ruby", "climax",
	"strike-pearl", "defend-pearl", "order-up",
	"strike-beryl", "defend-beryl", "release",
	"strike-spinelle", "defend-spinelle", "turn-around"
];

function defaultCollection () {

	return {
		cards: Object.fromEntries(starterCardKeys.map(key => [key, {}])),
		potions: [],
		relics: [],
		enemies: []
	}
}

function normalizeCardCollection (cards) {

	if (Array.isArray(cards))
		return Object.fromEntries(cards.filter(Boolean).map(key => [key, {}]));
	if (!cards || typeof cards !== 'object')
		return {};
	return Object.fromEntries(Object.entries(cards).filter(([key]) => key).map(([key, value]) => {
		if (!value || value === true)
			return [key, {}];
		if (typeof value === 'string')
			return [key, { selectedArt: value }];
		return [key, value];
	}));
}

function normalizeListCollection (entries) {

	if (Array.isArray(entries))
		return entries;
	if (!entries || typeof entries !== 'object')
		return [];
	return Object.keys(entries);
}

function normalizeCollection (source) {

	const defaults = defaultCollection();
	const normalized = Object.assign({}, defaults, source || {});
	normalized.cards = Object.assign({}, defaults.cards, normalizeCardCollection(normalized.cards));
	normalized.potions = normalizeListCollection(normalized.potions);
	normalized.relics = normalizeListCollection(normalized.relics);
	normalized.enemies = normalizeListCollection(normalized.enemies);
	return normalized;
}

const difficultyOptions = ["zen", "casual", "challenge", "hardcore"];
const fetishModeOptions = ["mature", "soft"];
const defaultFetishModes = Object.fromEntries(Object.values(champions).map(champion => [champion.key, "mature"]));
let champion = localStorage.getItem("champion");

function zenMilestone () {

	return Object.values(milestonelist[0] || {}).find(ms => ms.check && ms.check.runs);
}

function normalizeFetishModes (source) {

	if (!source || typeof source !== 'object')
		return Object.assign({}, defaultFetishModes);

	return Object.fromEntries(Object.keys(defaultFetishModes).map(key => [
		key,
		fetishModeOptions.includes(source[key]) ? source[key] : "mature"
	]));
}

if (!champion || !Object.values(champions).some(c => c.key === parseInt(champion, 10))) {
	champion = 1;
	localStorage.setItem("champion", 1);
} else
	champion = parseInt(champion, 10);

if (localStorage.getItem("user.save")) {
  try {
    let storage = JSON.parse(
      CryptoJS.AES.decrypt(
        localStorage.getItem("user.save"),
        process.env.REACT_APP_ENCRYPTION_KEY
      ).toString(CryptoJS.enc.Utf8)
    );

    if (storage.version && storage.data) {
      updateSaveData(storage);
      items = Object.assign({}, storage, { version, "version.previous": storage.version });
    }
  } catch (err) {
    console.error("Failed to load save data");
  }
}

export default class User {

	static get debugShowAllCollection () {

		return DEBUG_SHOW_ALL_COLLECTION;
	}

	static updateStorage () {

		localStorage.setItem("user.save", CryptoJS.AES.encrypt(JSON.stringify(items), process.env.REACT_APP_ENCRYPTION_KEY).toString());
	}

	static setItem (item, value) {

		items[item] = value;
		User.updateStorage();
	}

	static get difficulty () {

		return difficulty;
	}

	static set difficulty (value) {

		if (!User.isDifficultyUnlocked(value))
			value = "challenge";
		difficulty = value;
		User.setItem("difficulty", value);
		User.updateData();
	}

	static get champion () {

		let select = champion;
		if (select) {
			if (premium)
				return select;
			let runmodel = Library.getChampion(select);
			let runpremium = runmodel.premium;
			if (runpremium)
				return 1;
			return select;
		}
		return 1;
	}

	static set champion (value) {

		champion = value;
		localStorage.setItem("champion", value);
		User.updateStorage();
	}

	static getItem (item) {

		return items[item];
	}

	static get fetishModes () {

		return normalizeFetishModes(User.getItem("fetish.modes"));
	}

	static fetishModeFor (champion) {

		const key = champion && champion.key ? champion.key : champion;
		return User.fetishModes[key] || "mature";
	}

	static setFetishMode (champion, mode) {

		if (!fetishModeOptions.includes(mode))
			return;

		const key = champion && champion.key ? champion.key : champion;
		User.setItem("fetish.modes", Object.assign({}, User.fetishModes, { [key]: mode }));
	}

	static isFetishSoft (champion) {

		return User.fetishModeFor(champion) === "soft";
	}

	static isCutsceneSoftDisabled (cutscene) {

		return cutscene && cutscene.champion > 0 && User.isFetishSoft(cutscene.champion);
	}

	static lowerOrEqualDifficulties (difficulty) {

		const index = difficultyOptions.indexOf(difficulty);
		if (index < 0)
			return [difficulty];
		return difficultyOptions.slice(0, index + 1);
	}

	static applyDifficultyUnlock (difficulty, callback) {

		User.lowerOrEqualDifficulties(difficulty).forEach(callback);
	}

	static isDifficultyUnlocked (difficulty) {

		if (difficulty !== "zen")
			return true;
		const milestone = zenMilestone();
		return !milestone || User.checkMilestone(milestone.key) > 0;
	}

	static get currentRun () {

		let run = items["run.save"];
		if (run) {
			run = JSON.parse(run);
			return User.canAccessRun(run) ? run : undefined;
		}
	}

	static canAccessRun (run) {

		const model = Library.getChampion(run.champion.model);
		const limits = User.contentLimits(model.key);
		if ((!User.premium && model.premium) || run.power > limits.mazePower ||
			run.champion.level > limits.level || run.champion.maxLevel > limits.level ||
			(run.contentLimits && Object.keys(limits).some(key => run.contentLimits[key] > limits[key])))
			return false;
		return !(run.deck || []).some(entry => entry.card.champion === model.key && entry.card.level > limits.level);
	}

	/*static get championSelect () {

		let select = items["champion.select"];
		if (select) {
			if (premium)
				return select;
			let runmodel = Library.getChampion(select);
			let runpremium = runmodel.premium;
			if (runpremium)
				return 1;
			return select;
		}
		return 1;
	}*/

	static removeItem (item) {

		delete items[item];
		User.updateStorage();
	}

	static get data () {

		return User.dataFor(User.champion, User.difficulty);
	}

	static dataFor (champion, difficulty) {

		const dataclone = Object.assign({}, data[champion][difficulty]);
		const limits = User.contentLimits(champion);
		dataclone.progress = Math.min(dataclone.progress, limits.storyProgress);
		dataclone.maxLevel = User.unlockedLevel(champion, dataclone.progress);
		dataclone.level = Math.min(dataclone.level, dataclone.maxLevel);
		// Apply access limits to a view of the save, preserving subscriber unlocks.
		dataclone.devotion = Object.fromEntries(Object.entries(dataclone.devotion).map(([key, value]) => {
			const limit = devotionLimit(limits, key);
			return [key, { ...value, selected: Math.min(value.selected, value.rank, limit) }];
		}));

		return dataclone;
	}

	static contentLimits (champion, premium=User.premium) {

		return contentLimits(Library.getChampion(champion), premium);
	}

	static unlockedLevel (champion, progress) {

		return unlockedLevel(Library.getChampion(champion), progress, User.premium);
	}

	static storySceneLevels (champion) {

		return storySceneLevels(Library.getChampion(champion));
	}

	static devotionLimit (champion, devotion) {

		return devotionLimit(User.contentLimits(champion), devotion);
	}

	static canAccessCutscene (cutscene) {

		if (!cutscene || cutscene.champion <= 0)
			return !!cutscene;
		const limits = User.contentLimits(cutscene.champion);
		if (cutscene.type === "level up")
			return Number(cutscene.value) <= limits.level;
		const storyLevels = Library.getChampion(cutscene.champion).progression.storySceneLevels;
		const event = /^event(\d+)-/.exec(cutscene.value);
		return (!event || Number(event[1]) < limits.storyProgress) &&
			(!storyLevels[cutscene.value] || storyLevels[cutscene.value] <= limits.level);
	}

	static canAccessStoryEvent (event, champion) {

		const quest = Object.values(Library.quests).find(quest => quest.champion === champion &&
			JSON.stringify(quest.enemies) === JSON.stringify(event.enemies));
		return !!quest && quest.value < User.contentLimits(champion).storyProgress;
	}

	static get expGrowthLevelUp () {

		return 100;
	}

	static expForLevel (level) {

		return (level - 1) * User.expGrowthLevelUp;
	}

	static totalExpForLevel (level) {

		return User.expGrowthLevelUp * level * (level - 1) / 2;
	}

	static get databank () {

		return data;
	}

	static get cards () {

    	return User.cardsFor(User.champion, User.difficulty);
	}

	static cardsFor (champion, difficulty) {

    let cards = Object.values(Library.cards);
		cards = cards.filter(card => card.champion === champion && !card.token);
    cards = cards.filter(card => card.level && card.level <= User.dataFor(champion, difficulty).level);
    return cards;
	}

	static connectWithPatreon () {

		const authorizationUrl = `https://www.patreon.com/oauth2/authorize?response_type=code&client_id=${OAUTH_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;
    window.location.href = authorizationUrl;
	}

	static get collection () {

		return collection;
	}

	static hasCollected (type, key) {

		if (type === "cards")
			return !!collection.cards[key];
		if (!collection[type])
			collection[type] = [];
		return collection[type].includes(key);
	}

	static selectedCardArt (key) {

		const entry = collection.cards[key];
		return entry && typeof entry === 'object' ? entry.selectedArt : undefined;
	}

	static cardArtModeFor (champion) {

		const mode = User.getItem('card.art.modes')?.[champion];
		return ['manual', 'max-size', 'dynamic'].includes(mode) ? mode : 'manual';
	}

	static setCardArtMode (champion, mode) {

		if (!champion || !['manual', 'max-size', 'dynamic'].includes(mode))
			return;
		if (User.cardArtModeFor(champion) !== mode) {
			const notifications = Object.fromEntries(Object.entries(User.getItem('card.art.notifications') || {})
				.filter(([, owner]) => Number(owner) !== Number(champion)));
			User.setItem('card.art.notifications', notifications);
		}
		User.setItem('card.art.modes', Object.assign({}, User.getItem('card.art.modes'), { [champion]: mode }));
	}

	static hasCardArtNotification (key) {

		const champion = User.getItem('card.art.notifications')?.[key];
		return !!champion && User.cardArtModeFor(champion) === 'manual';
	}

	static hasCardArtNotifications (champion) {

		return User.cardsFor(champion, User.difficulty).some(card =>
			(User.debugShowAllCollection || User.knows('cards', card.key)) && User.hasCardArtNotification(card.key));
	}

	static clearCardArtNotification (key) {

		if (!User.getItem('card.art.notifications')?.[key])
			return;
		const notifications = { ...User.getItem('card.art.notifications') };
		delete notifications[key];
		User.setItem('card.art.notifications', notifications);
	}

	static notifyCardArtUnlocks (champion, previousLevel, level) {

		if (level <= previousLevel || User.cardArtModeFor(champion) !== 'manual')
			return;
		const notifications = { ...User.getItem('card.art.notifications') };
		let changed = false;
		Object.values(Library.rawcards).forEach(card => {
			if (card.champion !== champion || card.token || !card.level || card.level > level)
				return;
			if (Library.cardArts(card).some(art => Library.cardArtLevel(art) > previousLevel && Library.cardArtLevel(art) <= level)) {
				notifications[card.key] = champion;
				changed = true;
			}
		});
		if (changed)
			User.setItem('card.art.notifications', notifications);
	}

	static selectCardArt (key, img) {

		if (!key || !img)
			return;
		User.learn("cards", key);
		collection.cards[key] = Object.assign({}, collection.cards[key], { selectedArt: img });
		User.setItem('collection', JSON.stringify(collection));
		const card = Library.rawcards?.[key];
		if (card && User.hasCardArtNotification(key)) {
			const arts = Library.getUnlockedCardArts(card);
			const highestLevel = Math.max(...arts.map(art => Library.cardArtLevel(art)));
			if (arts.some(art => art.img === img && Library.cardArtLevel(art) === highestLevel))
				User.clearCardArtNotification(key);
		}
	}

	/*
	static get progress () {

		return progress;
	}

	static get traits () {

		return traits;
	}

	static get stats () {

		return stats;
	}

	static get shards () {

		return User.getItem("shards") || 0;
	}*/

	static get shards () {

		return data[champion][difficulty].shards || 0;
	}

	static shardsChange (value) {

		const shardCount = document.getElementById("shard-count");
		if (shardCount)
		  shardCount.textContent = value;
	}

	static setShards (value, champion, difficulty) {

		value = Math.min(value, 99999);
		const gained = Math.max(0, value - (data[champion][difficulty].shards || 0));
		data[champion][difficulty].shards = value;
		if (gained > 0) {
			User.lowerOrEqualDifficulties(difficulty)
				.filter(diff => diff !== difficulty)
				.forEach(diff => data[champion][diff].shards = Math.min(99999, (data[champion][diff].shards || 0) + gained));
		}
		User.updateData();
		User.shardsChange(value);
	}

	static setBenevolence (value, champion, difficulty) {

		value = Math.max(0, Math.min(value, 10));
		data[champion][difficulty].benevolence = value;
		User.updateData();
	}

	static unlockPremium () {

		premium = true;
	}

	static get premium () {

		return premium;
	}

	static save () {

			User.updateStorage();
			const encryptedData = localStorage.getItem("user.save");

    	//const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(items), process.env.REACT_APP_ENCRYPTION_KEY).toString();
    	const blob = new Blob([encryptedData], { type: 'text/plain;charset=utf-8' });
    	saveAs(blob, 'mazeofambition.save');
	}

	static load (file) {

	    if (file) {
	      const reader = new FileReader();
	      reader.onload = (e) => {
	        const encryptedData = e.target.result;
	        try {
	          const bytes = CryptoJS.AES.decrypt(encryptedData, process.env.REACT_APP_ENCRYPTION_KEY);
	          const originalData = bytes.toString(CryptoJS.enc.Utf8);
	          //localStorage.setItem('save', originalData);
	          const loaddata = JSON.parse(originalData);;
	          items = Object.assign({}, loaddata);
	          User.updateStorage();
	          console.log('Data loaded');
	          window.history.pushState(null, null, '/play')
	          window.location.reload(false);
	        } catch (error) {
	          console.error('Error decrypting file:', error);
	        }
	      };
	      reader.readAsText(file);
	    }
	}

	static clear () {

		items = { version };
		User.updateStorage();
		console.log('Data cleared');
		window.history.pushState(null, null, '/play')
		window.location.reload(false);
	}

	static addStats (difficulty, type, champion, value) {

		data[champion][difficulty].stats[type] += value;
		User.updateData();
	}

	static totalRunsPlayed () {

		return Object.values(data).reduce((total, championData) => {
			return total + difficultyOptions.reduce((difficultyTotal, difficulty) => {
				return difficultyTotal + (championData[difficulty]?.stats?.runsPlayed || 0);
			}, 0);
		}, 0);
	}

	static unlockRunMilestones () {

		return Object.values(milestonelist[0] || {})
			.filter(milestone => milestone.check && milestone.check.runs && User.totalRunsPlayed() >= milestone.check.runs)
			.map(milestone => User.unlockMilestone(milestone.key))
			.filter(Boolean);
	}

	static addRun (run) {

    //const runStats = run.stats;
		const championkey = run.champion.model.key || run.champion.model;
		User.addStats(run.difficultySetting, "runsPlayed", championkey, 1);
        //User.addStats(run.difficultySetting, "roomsCleared", run.champion.model.key, runStats.roomsCleared);
        //User.addStats(run.difficultySetting, "bossesSlain", run.champion.model.key, runStats.bossesSlain);
        const championStats = data[championkey][run.difficultySetting].stats;
		championStats.runs.push({
			deck: run.deck.map(c => { return { status: c.status, key: c.card.key } }),
			relics: run.champion.relics.map(r => r.key || r),
			power: run.power,
			companion: run.companion ? (run.companion.model.key || run.companion.model) : null,
			minDeckSize: run.minDeckSize,
			stats: run.stats || {
				champion: run.champion.model,
				level: run.champion.levelReached
			},
    	date: new Date().toISOString()
		})
		if (championStats.runs.length > 20)
   			 championStats.runs.shift();
		User.updateData();

		const undo = () => {

			User.addStats(run.difficultySetting, "runsPlayed", run.champion.model.key, -1);
	        //User.addStats(run.difficultySetting, "roomsCleared", run.champion.model.key, -runStats.roomsCleared);
	        //User.addStats(run.difficultySetting, "bossesSlain", run.champion.model.key, -runStats.bossesSlain);
			championStats.runs.pop();
			User.updateData();
		}
		undo.unlockedMilestones = User.unlockRunMilestones();
		return undo;
	}

	static championGrowth (difficulty, key, level) {

		const previousLevel = User.dataFor(key, difficulty).level;
		User.applyDifficultyUnlock(difficulty, diff => {
			if (data[key][diff].level < level)
				data[key][diff].level = level;
		});
		User.updateData();
		User.notifyCardArtUnlocks(key, previousLevel, User.dataFor(key, difficulty).level);
		return [];
	}

	static setDevotion (difficulty, key, devotion, value) {

		value = Math.max(1, Math.min(value, User.devotionLimit(key, devotion)));
		if (data[key][difficulty].devotion[devotion].rank < value)
			return;
		data[key][difficulty].devotion[devotion].selected = value;
		User.updateData();
	}

	static devotionRequirement (rank) {

		return rank * (rank + 1) * 50;
	}

	static metCelestials () {

		return User.getItem("metCelestials") || [];
	}

	static hasMetCelestial (celestial) {

		return User.metCelestials().includes(celestial);
	}

	static meetCelestial (...args) {

		const celestial = args[args.length - 1];
		if (!celestial)
			return;
		const celestials = User.metCelestials();
		if (celestials.includes(celestial))
			return;
		User.setItem("metCelestials", celestials.concat(celestial));
		const milestone = Object.values(milestonelist[0] || {}).find(ms => ms.check && ms.check.celestial === celestial);
		return milestone ? User.unlockMilestone(milestone.key) : null;
	}

	static addDevotionProgress (difficulty, key, devotion, exp) {

		const devotionData = data[key][difficulty].devotion[devotion];
		if (devotion === "maze" && devotionData.rank >= User.devotionLimit(key, devotion))
			return;
		devotionData.progress += exp;
		if (devotionData.progress >= User.devotionRequirement(devotionData.rank)) {
			devotionData.rank++;
			devotionData.progress = 0;
			if (devotionData.selected === devotionData.rank - 1)
				devotionData.selected++;
		}
	}

	static addDevotionExp (difficulty, key, devotion, exp, carryDown=false) {

		exp = Math.max(0, Math.round(exp || 0));
		if (exp <= 0)
			return;
		const difficulties = carryDown ? User.lowerOrEqualDifficulties(difficulty) : [difficulty];
		difficulties.forEach(diff => User.addDevotionProgress(diff, key, devotion, exp));
		User.updateData();
	}

	static setDevotionRankAtLeast (difficulty, key, devotion, rank) {

		const playableLimit = User.devotionLimit(key, devotion);
		// Completing the last released maze still unlocks the next, unplayable power.
		if (devotion === "maze")
			rank = Math.min(rank, playableLimit + 1);
		const devotionData = data[key][difficulty].devotion[devotion];
		if (devotionData.rank >= rank)
			return;
		const previousRank = devotionData.rank;
		devotionData.rank = rank;
		devotionData.progress = 0;
		if (devotionData.selected === previousRank)
			devotionData.selected = Math.min(rank, playableLimit);
	}

	static rankUpDevotion (difficulty, key, devotion, carryDown=false) {

		const devotionData = data[key][difficulty].devotion[devotion];
		const rank = devotionData.rank + 1;
		if (carryDown)
			User.lowerOrEqualDifficulties(difficulty).forEach(diff => User.setDevotionRankAtLeast(diff, key, devotion, rank));
		else
			User.setDevotionRankAtLeast(difficulty, key, devotion, rank);
		User.updateData();
	}

	static upgradeCard (difficulty, key, card) {

		data[key][difficulty].upgrades[card] = data[key][difficulty].upgrades[card] || 0;
		data[key][difficulty].upgrades[card]++;
		User.updateData();
	}

	static unlockStage (difficulty, key) {

		return;
	}

	static progressStory (difficulty, key) {
		
		const quest = User.questFor(difficulty, key);
		if (!quest)
			return;

		const progress = quest.value + 1;
		User.applyDifficultyUnlock(difficulty, diff => {
			if (data[key][diff].progress >= progress)
				return;
			data[key][diff].progress = progress;
		});
		//data[key][difficulty].quest = this.questFor(difficulty, key).next;
		User.updateData();
	}

	static get dataQuest () {

		return User.questFor(User.difficulty, User.champion);
	}

	static questFor (difficulty, key) {

		const progress = User.dataFor(key, difficulty).progress;
		if (progress >= User.contentLimits(key).storyProgress)
			return null;
		return Object.values(Library.quests).find(quest => quest.value === progress && quest.champion === key) || null;
	}

	static updateData () {

		User.setItem('data', JSON.stringify(data));
	}

	static hasSeen (cutscene) {

		return gallery.includes(cutscene);
	}

	static watch (cutscene) {

		if (this.hasSeen(cutscene))
			return;
		gallery.push(cutscene);
		User.setItem('gallery', JSON.stringify(gallery));
	}

	static checkMilestone (milestone) {

		return milestones[milestone] || 0;
	}

	static getMilestoneEntry (milestone) {

		let milestoneEntry;
		let championKey;
		Object.entries(milestonelist).some(([key, list]) => {
			milestoneEntry = Object.values(list).find(ms => ms.key === milestone);
			if (milestoneEntry) {
				championKey = parseInt(key, 10);
				return true;
			}
			return false;
		});
		return { milestoneEntry, championKey };
	}

	static isGlobalMilestone (milestone) {

		return User.getMilestoneEntry(milestone).championKey === 0;
	}

	static unlockMilestone (milestone, difficulty) {

		const { milestoneEntry, championKey } = User.getMilestoneEntry(milestone);

		if (!milestoneEntry)
			return;

		if (championKey === 0) {
			if (this.checkMilestone(milestone))
				return;
			milestones[milestone] = 1;
			User.setItem('milestones', JSON.stringify(milestones));
			return milestoneEntry;
		}

		let diffvalues = { "zen": 1, "casual": 2, "challenge": 3, "hardcore": 4 }

		if (this.checkMilestone(milestone) >= diffvalues[difficulty])
			return;
		milestones[milestone] = diffvalues[difficulty];
		User.setItem('milestones', JSON.stringify(milestones));

		if (!milestoneEntry || !milestoneEntry.unlock || !championKey)
			return milestoneEntry;

		switch (milestoneEntry.unlock.type) {
		case "growth":
			if (milestoneEntry.unlock.value > User.dataFor(championKey, difficulty).maxLevel)
				User.progressStory(difficulty, championKey);
			break;
		default:
			break;
		}

		return milestoneEntry;
	}

	static knows (type, key) {

		return User.hasCollected(type, key);
	}

	static learn (type, key) {

		if (!key)
			return;
		if (User.hasCollected(type, key))
			return;
		if (type === "cards")
			collection.cards[key] = {};
		else {
			if (!collection[type])
				collection[type] = [];
			collection[type].push(key);
		}
		User.setItem('collection', JSON.stringify(collection));
	}
}

let difficulty = User.getItem("difficulty");

if (!difficulty || !difficultyOptions.includes(difficulty))
	difficulty = "challenge";

let data = User.getItem('data') ? JSON.parse(User.getItem('data')) : {}

Object.values(champions).forEach(champion => {

	if (!data[champion.key])
		data[champion.key] = {};

	const championData = data[champion.key];

	difficultyOptions.forEach(diff => {

		if (!championData[diff])
			championData[diff] = initialChampionData(champion);
		delete championData[diff].runes;
		delete championData[diff].soul;
		delete championData[diff].traits;
		delete championData[diff].selectedTraits;
		if (!championData[diff].devotion)
			championData[diff].devotion = {};
		["maze", "exp", "passive", "health", "cards"].forEach(devotion => {
			if (!championData[diff].devotion[devotion])
				championData[diff].devotion[devotion] = { rank: 1, selected: 1, progress: 0 };
		});
	})
})

let metCelestials = User.getItem("metCelestials") || [];
Object.values(data).forEach(championData => {
	difficultyOptions.forEach(diff => {
		const difficultyData = championData[diff];
		if (!difficultyData)
			return;
		if (difficultyData.metCelestials) {
			metCelestials = metCelestials.concat(difficultyData.metCelestials.filter(celestial => !metCelestials.includes(celestial)));
			delete difficultyData.metCelestials;
		}
	})
})
User.setItem("metCelestials", metCelestials);
User.updateData();

let gallery = User.getItem('gallery') ? JSON.parse(User.getItem('gallery')) : []
//User.watch(-1)
let milestones = User.getItem('milestones') ? JSON.parse(User.getItem('milestones')) : {}
Object.values(milestonelist[0] || {}).forEach(milestone => {
	if (milestones[milestone.key])
		milestones[milestone.key] = 1;
});
metCelestials.forEach(celestial => {
	const milestone = Object.values(milestonelist[0] || {}).find(ms => ms.check && ms.check.celestial === celestial);
	if (milestone && !milestones[milestone.key])
		milestones[milestone.key] = 1;
});
Object.values(milestonelist[0] || {}).forEach(milestone => {
	if (milestone.check && milestone.check.runs && User.totalRunsPlayed() >= milestone.check.runs)
		milestones[milestone.key] = 1;
});
User.setItem('milestones', JSON.stringify(milestones));
if (!User.isDifficultyUnlocked(difficulty))
	User.difficulty = "challenge";
let collection = normalizeCollection(User.getItem('collection') ? JSON.parse(User.getItem('collection')) : null);
User.setItem('collection', JSON.stringify(collection));
User.setItem('fetish.modes', User.fetishModes);

/*Object.keys(cutscenes).forEach(key => {
	let cs = cutscenes[key];
	if (cs.type === "level up" && cs.value <= User.dataFor(cs.champion, "challenge").level)
		User.watch(cs.key)
})*/
